import { HttpBackend, HttpClient, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { WINDOW } from '../services/window.service';
import { Observable, map, shareReplay } from 'rxjs';
import { BulkPlanDetailsResponse } from 'src/app/model/plan/plan-detail';

/**
 * Asset served in place of the live plan-details call. Shaped exactly like the real
 * `bulkplandetails` response — `{ plans: { count, data }, claims }` — because the committed
 * fixture is a captured snapshot of that endpoint, root-level `claims` included.
 */
const MOCK_ASSET_URL = 'assets/mock-plan-details.json';

/**
 * Serves `src/assets/mock-plan-details.json` instead of calling the plan-details endpoint,
 * so the multi-policy claim flow can be exercised locally against a fixed dataset.
 *
 * Two gates, BOTH required:
 *   1. the app is running on localhost, and
 *   2. `window.Buildconfig.useMockPlanDetails === true` (`false` in source control).
 *
 * The hostname check is the backstop: even if the flag is committed as `true` by mistake, or
 * flipped by a release pipeline token substitution, no deployed environment can ever serve
 * fixture data in place of a real customer's policies. `localhost` matches the app's own
 * `isLocalhost` definition in index.html, so this toggle is live in exactly the same
 * situations as `localApi` / `localSiteId`.
 *
 * Both are read per request rather than cached at construction, so the flag can be flipped
 * from the console without a reload.
 *
 * Handles all three routes the app uses, because they do NOT share a response shape:
 *   - `plandetails?planNumber=X` -> single-plan array, filtered to that plan
 *   - `plandetails?refresh=`     -> the whole array (getAllPlanDetails)
 *   - `bulkplandetails`          -> re-wrapped in the `{ plans: { count, data }, claims }`
 *                                   envelope that getBulkPlanDetails() parses
 *
 * Intentionally does NOT intercept anything else: CMS, Okta, claims and equipment writes all
 * still hit their real backends, so only the plan payload is substituted.
 */
@Injectable()
export class MockPlanDetailsInterceptor implements HttpInterceptor {
  // HttpBackend, not HttpClient: injecting HttpClient into an HTTP_INTERCEPTORS provider is a
  // cyclic dependency (HttpClient is built FROM the interceptor chain). Going straight to the
  // backend also keeps the asset fetch out of the chain, so it can't re-enter this interceptor.
  private readonly httpBackend = inject(HttpBackend);
  private readonly assetClient = new HttpClient(this.httpBackend);

  // Injected via the app's existing WINDOW token rather than touching the global directly:
  // `window.location` is non-configurable, so a global reference cannot be stubbed in tests.
  private readonly window = inject<Window>(WINDOW);

  /** Loaded once per session; the asset is static. */
  private mockData$?: Observable<BulkPlanDetailsResponse>;

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.isEnabled() || req.method !== 'GET') {
      return next.handle(req);
    }

    // Order matters: 'bulkplandetails' also contains the substring 'plandetails'.
    const path = req.url.split('?')[0].toLowerCase();
    const isBulk = path.endsWith('bulkplandetails');
    const isPlanDetails = path.endsWith('plandetails');

    if (!isBulk && !isPlanDetails) {
      return next.handle(req);
    }

    return this.loadMockData().pipe(
      map(({ plans, claims }) => {
        if (isBulk) {
          const body: BulkPlanDetailsResponse = { plans, claims };
          return new HttpResponse({ status: 200, url: req.url, body });
        }

        const planNumber = req.params.get('planNumber');
        const body = planNumber ? plans.data.filter((plan) => plan.planNumber === planNumber) : plans.data;
        return new HttpResponse({ status: 200, url: req.url, body });
      })
    );
  }

  private isEnabled(): boolean {
    if (this.window?.location?.hostname !== 'localhost') {
      return false;
    }
    return (this.window as unknown as { Buildconfig?: { useMockPlanDetails?: boolean } })?.Buildconfig?.useMockPlanDetails === true;
  }

  private loadMockData(): Observable<BulkPlanDetailsResponse> {
    this.mockData$ ??= this.assetClient.get<BulkPlanDetailsResponse>(MOCK_ASSET_URL).pipe(shareReplay({ bufferSize: 1, refCount: false }));
    return this.mockData$;
  }
}
